/**
 * A PDF's Word copy, split by who needs what. `useConversionActions` holds
 * the state and the actions (use the copy, go back to the PDF, convert again,
 * the downloads, the side-by-side compare). `ConversionCallout` speaks up
 * only when the copy needs a decision: parts were guessed, it is not in use,
 * or it could not be made. `ConversionMenuItems` puts the rest in the
 * drawer's "..." menu; `ConversionCompareDialog` holds the compare and the
 * revert confirm; `TechnicalDetails` is the converter's own numbers, for
 * CaseBlink staff.
 */
import * as Sentry from "@sentry/react";
import {
  ArrowLeftRight,
  Download,
  FileOutput,
  FileWarning,
  Loader2,
  RefreshCw,
  ScanSearch,
  Undo2
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FC } from "react";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { PdfPreview } from "@/components/common/PdfPreview";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import {
  CONVERSION_COUNT_LABEL,
  conversionBadgeOf,
  conversionSpots,
  conversionSummary,
  percentLabel,
  plainConversionNote,
  type ConversionSpot
} from "../model";
import type { ConversionReport, FirmLibraryEntry } from "../types";
import { EntryCallout } from "./EntryCallout";

const download = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const stemOf = (entry: FirmLibraryEntry): string =>
  (entry.original_filename ?? entry.name ?? entry.id).replace(/\.[^.]+$/, "");

type PageState = "idle" | "loading" | "ready" | "missing" | "error";

type Pages = { key: string; blob: Blob | null; state: PageState };

/** One PDF blob fetched lazily; 404 is "not there (yet)", not an error. */
export const usePages = (
  enabled: boolean,
  key: string,
  fetcher: () => Promise<Blob>
): { blob: Blob | null; state: PageState } => {
  const [pages, setPages] = useState<Pages>({
    key,
    blob: null,
    state: "idle"
  });
  // The fetcher closes over props; keep the latest without retriggering the
  // effect (a re-render mid-fetch must not cancel the fetch).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const current = pages.key === key ? pages : null;
  const started = current !== null && current.state !== "idle";
  useEffect(() => {
    if (!enabled || started) return undefined;
    let cancelled = false;
    setPages({ key, blob: null, state: "loading" });
    fetcherRef
      .current()
      .then((data) => {
        if (!cancelled) setPages({ key, blob: data, state: "ready" });
      })
      .catch((error: { status?: number }) => {
        if (cancelled) return;
        if (error?.status !== 404) Sentry.captureException(error);
        setPages({
          key,
          blob: null,
          state: error?.status === 404 ? "missing" : "error"
        });
      });
    return () => {
      cancelled = true;
      // Hidden or re-keyed mid-fetch: the next show must fetch again.
      setPages((prev) =>
        prev.key === key && prev.state === "loading"
          ? { ...prev, state: "idle" }
          : prev
      );
    };
    // `started` is derived from the state this effect writes; depending on it
    // would cancel the fetch the moment it starts.
  }, [enabled, key]);
  return current ?? { blob: null, state: "idle" };
};

/** Where both panes should scroll; `nonce` re-fires a jump to the same page. */
type Jump = { page: number; nonce: number };

type Run = (
  action: () => Promise<FirmLibraryEntry | void>,
  done?: string
) => Promise<void>;

export const useConversionActions = ({
  entry,
  scope,
  run,
  onQueued
}: {
  entry: FirmLibraryEntry;
  scope: FirmScope;
  /** Runs an action with the drawer's shared busy/toast handling. */
  run: Run;
  onQueued: () => void;
}) => {
  const { conversion } = entry;
  const status = conversion?.status ?? null;
  const promoted = status === "promoted";
  // A Word copy that exists but is not the entry's file: the firm kept the
  // PDF, parts were guessed and it waits to be checked, or the automatic
  // promotion did not go through.
  const unusedCopy = status === "ready" || status === "review";
  const hasCopy = unusedCopy || promoted;
  const [compareOpen, setCompareOpen] = useState(false);
  const [jump, setJump] = useState<Jump | null>(null);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [report, setReport] = useState<ConversionReport | null>(null);

  // A new copy (re-run, promote, revert) invalidates the pages and the report.
  const generation = `${entry.id}:${conversion?.generated_at ?? ""}:${
    conversion?.status ?? ""
  }`;
  useEffect(() => {
    setReport(null);
    setCompareOpen(false);
    setJump(null);
  }, [generation]);

  useEffect(() => {
    if (!hasCopy || !conversion?.has_report || report) return undefined;
    let cancelled = false;
    firmKnowledgeApi
      .getEntryConversion(entry.id, scope)
      .then((data) => {
        if (!cancelled) setReport(data.report);
      })
      .catch((error) => Sentry.captureException(error));
    return () => {
      cancelled = true;
    };
  }, [hasCopy, conversion?.has_report, report, entry.id, scope]);

  const spots = useMemo(
    () => (hasCopy ? conversionSpots(conversion, report) : []),
    [hasCopy, conversion, report]
  );

  const kind = conversionBadgeOf(entry);
  const coverage = conversion?.coverage;

  return {
    entry,
    scope,
    status,
    promoted,
    unusedCopy,
    hasCopy,
    report,
    spots,
    generation,
    /** The copy needs someone to look, or could not be made. */
    needsDecision:
      kind === "check" ||
      kind === "review" ||
      kind === "unused" ||
      status === "failed",
    canConvert: Boolean(entry.is_pdf) && !hasCopy && status !== "pending",
    coverageText:
      coverage === null || coverage === undefined
        ? null
        : coverage >= 0.995
        ? "100% of the words came through"
        : `${percentLabel(coverage)} of the words came through`,
    coverageLow: (coverage ?? 1) < 0.95,
    verifyProblems: Boolean(report?.verify && !report.verify.ok),
    compareOpen,
    setCompareOpen,
    jump,
    setJump,
    openCompare: (page: number | null) => {
      setCompareOpen(true);
      if (page) setJump({ page, nonce: Date.now() });
    },
    confirmRevert,
    setConfirmRevert,
    downloadCopy: () =>
      run(async () => {
        const blob = await firmKnowledgeApi.getConversionDocx(entry.id, scope);
        download(blob, `${stemOf(entry)}.docx`);
      }),
    downloadOriginalPdf: () =>
      run(async () => {
        const blob = await firmKnowledgeApi.getConversionSourcePdf(
          entry.id,
          scope
        );
        download(blob, `${stemOf(entry)}.pdf`);
      }),
    convertAgain: () =>
      run(async () => {
        await firmKnowledgeApi.convertEntry(entry.id, scope);
        onQueued();
        return firmKnowledgeApi.getEntry(entry.id, scope);
      }, "Converting to Word."),
    useCopy: () =>
      run(async () => {
        const updated = await firmKnowledgeApi.promoteConversion(
          entry.id,
          scope
        );
        onQueued();
        return updated;
      }, "The Word copy is now the document. Agents will copy and edit it."),
    revert: () =>
      run(async () => {
        const updated = await firmKnowledgeApi.revertConversion(
          entry.id,
          scope
        );
        onQueued();
        return updated;
      }, "The original PDF is the document again.")
  };
};

export type ConversionControls = ReturnType<typeof useConversionActions>;

const SpotList: FC<{
  spots: ConversionSpot[];
  onJump: (page: number) => void;
  activePage?: number | null;
}> = ({ spots, onJump, activePage }) => (
  <ul className="space-y-0.5" data-testid="conversion-spots">
    {spots.map((spot, index) => {
      const body = (
        <>
          <span
            className={cn(
              "w-14 shrink-0 font-medium",
              spot.page ? "text-fg" : "text-fg-3"
            )}
          >
            {spot.page ? `Page ${spot.page}` : "Overall"}
          </span>
          <span className="min-w-0 break-words">{spot.text}</span>
        </>
      );
      return (
        <li key={`${spot.page ?? "doc"}-${index}`}>
          {spot.page ? (
            <button
              type="button"
              className={cn(
                "flex w-full gap-2 rounded-md px-1.5 py-1 text-left text-sm hover:bg-fill-1",
                activePage === spot.page && "bg-fill-2"
              )}
              onClick={() => onJump(spot.page as number)}
              data-testid={`conversion-spot-${index}`}
            >
              {body}
            </button>
          ) : (
            <div className="flex gap-2 px-1.5 py-1 text-sm">{body}</div>
          )}
        </li>
      );
    })}
  </ul>
);

const CALLOUT_TITLE = {
  check: "Check the Word copy against the PDF",
  review: "The Word copy needs a look",
  unused: "A Word copy is ready but not in use"
} as const;

/** Only when the copy needs a decision; editors only. */
export const ConversionCallout: FC<{
  conversion: ConversionControls;
  canEdit: boolean;
  busy: boolean;
}> = ({ conversion: c, canEdit, busy }) => {
  if (!canEdit || !c.needsDecision) return null;

  if (c.status === "failed") {
    return (
      <EntryCallout
        tone="warn"
        icon={FileWarning}
        title="Couldn't make a Word copy"
        data-testid="conversion-callout"
        actions={
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => void c.convertAgain()}
            data-testid="conversion-callout-retry"
          >
            <RefreshCw /> Try Again
          </Button>
        }
      >
        <p>
          {c.entry.conversion?.error ? `${c.entry.conversion.error}. ` : ""}
          Until then, the agents reproduce the PDF.
        </p>
      </EntryCallout>
    );
  }

  const kind = c.promoted
    ? "check"
    : c.entry.conversion?.status === "review"
    ? "review"
    : "unused";
  const pageSpots = c.spots.filter((spot) => spot.page).length;
  const summary = [
    c.coverageText,
    pageSpots
      ? `${pageSpots} ${pageSpots === 1 ? "spot" : "spots"} to check`
      : null
  ].filter(Boolean);

  return (
    <EntryCallout
      tone="warn"
      icon={ScanSearch}
      title={CALLOUT_TITLE[kind]}
      data-testid="conversion-callout"
      actions={
        <>
          <Button
            variant="secondary"
            onClick={() => c.openCompare(null)}
            data-testid="conversion-callout-compare"
          >
            <ArrowLeftRight /> Compare
          </Button>
          {c.unusedCopy ? (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => void c.useCopy()}
              data-testid="conversion-callout-promote"
            >
              <FileOutput /> Use Word Copy
            </Button>
          ) : (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => c.setConfirmRevert(true)}
              data-testid="conversion-callout-revert"
            >
              <Undo2 /> Keep the PDF
            </Button>
          )}
        </>
      }
    >
      {summary.length ? (
        <p>
          <span
            className={cn(c.coverageLow && "font-medium text-warn")}
            data-testid="conversion-coverage"
          >
            {summary.join(" · ")}
          </span>
        </p>
      ) : null}
      {kind === "unused" && !c.spots.length ? (
        <p>Until you use it, the agents reproduce the PDF.</p>
      ) : null}
      {c.spots.length ? (
        <SpotList spots={c.spots} onJump={(page) => c.openCompare(page)} />
      ) : null}
      {c.verifyProblems ? (
        <p className="text-warn">
          Word reported problems opening the copy; open it in Word before
          relying on it.
        </p>
      ) : null}
    </EntryCallout>
  );
};

/** The Word copy's entries in the "..." menu, led by a separator. */
export const ConversionMenuItems: FC<{
  conversion: ConversionControls;
  canEdit: boolean;
  busy: boolean;
}> = ({ conversion: c, canEdit, busy }) => {
  const items = [
    c.hasCopy ? (
      <DropdownMenuItem
        key="compare"
        onSelect={() => c.openCompare(null)}
        data-testid="conversion-compare"
      >
        <ArrowLeftRight /> Compare With the Original PDF
      </DropdownMenuItem>
    ) : null,
    canEdit && c.unusedCopy ? (
      <DropdownMenuItem
        key="promote"
        disabled={busy}
        onSelect={() => void c.useCopy()}
        data-testid="conversion-promote"
      >
        <FileOutput /> Use Word Copy
      </DropdownMenuItem>
    ) : null,
    c.promoted ? (
      <DropdownMenuItem
        key="pdf"
        disabled={busy}
        onSelect={() => void c.downloadOriginalPdf()}
        data-testid="conversion-download-pdf"
      >
        <Download /> Download Original PDF
      </DropdownMenuItem>
    ) : c.hasCopy ? (
      <DropdownMenuItem
        key="docx"
        disabled={busy}
        onSelect={() => void c.downloadCopy()}
        data-testid="conversion-download-docx"
      >
        <Download /> Download Word Copy
      </DropdownMenuItem>
    ) : null,
    canEdit && c.promoted ? (
      <DropdownMenuItem
        key="revert"
        disabled={busy}
        onSelect={() => c.setConfirmRevert(true)}
        data-testid="conversion-revert"
      >
        <Undo2 /> Back to the PDF…
      </DropdownMenuItem>
    ) : null,
    canEdit && c.unusedCopy ? (
      <DropdownMenuItem
        key="again"
        disabled={busy}
        onSelect={() => void c.convertAgain()}
      >
        <RefreshCw /> Convert Again
      </DropdownMenuItem>
    ) : null,
    canEdit && c.canConvert ? (
      <DropdownMenuItem
        key="convert"
        disabled={busy}
        onSelect={() => void c.convertAgain()}
        data-testid="conversion-convert"
      >
        <RefreshCw />
        {c.status === "failed" ? "Try Converting Again" : "Convert to Word"}
      </DropdownMenuItem>
    ) : null
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <>
      <DropdownMenuSeparator />
      {items}
    </>
  );
};

const Pane: FC<{
  title: string;
  blob: Blob | null;
  state: PageState;
  missingText: string;
  jump: Jump | null;
}> = ({ title, blob, state, missingText, jump }) => (
  <div className="flex min-h-0 flex-col overflow-hidden rounded-md bg-fill-1 ring-1 ring-line-2">
    <div className="border-b border-line-2 bg-elevated px-2 py-1 text-sm font-medium text-fg-3">
      {title}
    </div>
    <div className="min-h-0 flex-1">
      {state === "ready" && blob ? (
        <PdfPreview
          file={blob}
          ariaLabel={title}
          showControls={false}
          defaultZoomMode="page-width"
          scrollToPageIndex={jump ? jump.page - 1 : null}
          scrollToPageKey={jump?.nonce ?? null}
        />
      ) : (
        <div className="flex h-full items-center justify-center p-4 text-center text-sm text-fg-3">
          {state === "loading" || state === "idle" ? (
            <Loader2 className="size-4 animate-spin" />
          ) : state === "missing" ? (
            missingText
          ) : (
            "Could not load these pages."
          )}
        </div>
      )}
    </div>
  </div>
);

/** The side-by-side compare and the "Back to the PDF" confirm. */
export const ConversionCompareDialog: FC<{
  conversion: ConversionControls;
}> = ({ conversion: c }) => {
  const { entry, scope, promoted, hasCopy, spots, jump } = c;
  const showPanes = c.compareOpen && hasCopy;
  const original = usePages(showPanes, c.generation, () =>
    firmKnowledgeApi.getConversionSourcePdf(entry.id, scope)
  );
  // Once promoted the copy is the entry's file, edits included: show its
  // rendition rather than the copy as first converted, re-fetched when a
  // saved edit has been rendered again.
  const copyKey = promoted
    ? `${c.generation}:${entry.rendering_status}`
    : c.generation;
  const copy = usePages(showPanes, copyKey, () =>
    promoted
      ? firmKnowledgeApi.getEntryPdf(entry.id, scope)
      : firmKnowledgeApi.getConversionPdf(entry.id, scope)
  );

  return (
    <>
      <Dialog open={showPanes} onOpenChange={c.setCompareOpen}>
        <DialogContent
          className="flex h-[92vh] w-[96vw] max-w-[96vw]! flex-col gap-3 p-4 sm:max-w-[96vw]!"
          data-testid="conversion-compare-dialog"
        >
          <DialogHeader className="space-y-0.5 pr-8">
            <DialogTitle className="text-base">
              {entry.name || stemOf(entry)}: Compare With the Original PDF
            </DialogTitle>
            <DialogDescription className="text-sm">
              The original PDF on the left,{" "}
              {promoted ? "this Word document" : "the Word copy"} on the right.
              {spots.some((spot) => spot.page)
                ? " Pick a spot to jump both to its page; Word pages can run a little ahead or behind."
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div
            className={cn(
              "grid min-h-0 flex-1 gap-3",
              spots.length ? "grid-cols-[16rem_1fr]" : "grid-cols-1"
            )}
          >
            {spots.length ? (
              <div className="min-h-0 overflow-y-auto rounded-md bg-warn-soft p-2 ring-1 ring-warn-line">
                <p className="mb-1 px-1.5 text-sm font-medium text-warn">
                  Check These Spots
                </p>
                <SpotList
                  spots={spots}
                  onJump={(page) => c.setJump({ page, nonce: Date.now() })}
                  activePage={jump?.page ?? null}
                />
              </div>
            ) : null}
            <div
              className="grid min-h-0 grid-cols-2 gap-3"
              data-testid="conversion-compare-panes"
            >
              <Pane
                title="Original PDF"
                blob={original.blob}
                state={original.state}
                missingText="The original PDF is not available."
                jump={jump}
              />
              <Pane
                title={promoted ? "This Word Document" : "Word Copy"}
                blob={copy.blob}
                state={copy.state}
                missingText="These pages are still being prepared."
                jump={jump}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={c.confirmRevert} onOpenChange={c.setConfirmRevert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Go Back to the Original PDF?</AlertDialogTitle>
            <AlertDialogDescription>
              The agents will reproduce the original PDF instead of copying and
              editing this Word document, and any edits saved to the Word
              document are discarded. The entry stays a PDF until you choose
              &ldquo;Use Word Copy&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep the Word Document</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                c.setConfirmRevert(false);
                void c.revert();
              }}
              data-testid="conversion-revert-confirm"
            >
              Back to the PDF
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/** What the converter itself reported, for CaseBlink staff. */
export const TechnicalDetails: FC<{
  conversion: ConversionControls;
}> = ({ conversion: c }) => {
  const { conversion } = c.entry;
  if (!c.entry.is_pdf && !conversion) return null;
  const counts = CONVERSION_COUNT_LABEL.filter(
    ([key]) => (conversion?.counts?.[key] ?? 0) > 0
  );
  const notes = [
    ...(conversion?.flags ?? []),
    ...(c.report?.flags ?? [])
  ].filter((note, index, all) => all.indexOf(note) === index);
  const guessed = (c.report?.blocks ?? []).filter(
    (block) => block.confidence < 0.8 || block.flags?.length
  );
  const confidence = percentLabel(conversion?.confidence);
  const tagged = conversion?.tagged ?? null;
  const facts = [
    c.coverageText,
    conversion?.pages
      ? `${conversion.pages} ${conversion.pages === 1 ? "page" : "pages"}`
      : null,
    ...counts.map(([key, label]) => `${conversion?.counts[key]} ${label}`)
  ].filter(Boolean);

  return (
    <div
      className="space-y-1.5 text-sm text-fg-2"
      data-testid="conversion-technical-panel"
    >
      <p>{conversionSummary(conversion)}</p>
      {c.hasCopy && facts.length ? <p>{facts.join(" · ")}</p> : null}
      {confidence || tagged !== null ? (
        <p className="text-fg-3">
          {confidence ? `Converter confidence ${confidence}. ` : ""}
          {tagged === null
            ? ""
            : tagged
            ? "Read from the PDF's document structure."
            : "Rebuilt from the page geometry (the PDF had no structure tags)."}
        </p>
      ) : null}
      {notes.length ? (
        <ul className="list-disc space-y-0.5 pl-4 text-fg-3">
          {notes.map((note) => (
            <li key={note}>
              {note}
              {plainConversionNote(note) === null ? " (no action needed)" : ""}
            </li>
          ))}
        </ul>
      ) : null}
      {guessed.length ? (
        <ul className="max-h-48 space-y-0.5 overflow-auto font-mono text-sm text-fg-3">
          {guessed.slice(0, 60).map((block, index) => (
            <li key={index} className="flex gap-2">
              <span className="shrink-0">
                p{block.page ?? "?"} · {block.kind} ·{" "}
                {Math.round(block.confidence * 100)}%
              </span>
              <span className="truncate">
                {block.flags?.length
                  ? block.flags.join("; ")
                  : block.text ?? ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
